/**
 * AUTH-13 — the frontend half of the tier-gate response contract. The server's
 * requireTier() / requireAdmin() reply with a stable envelope:
 *   401 { error, code: 'AUTH_REQUIRED' }                       — not signed in
 *   402 { error, code: 'UPGRADE_REQUIRED', requiredTier, currentTier } — under-tier
 *   403 { error, code: 'ADMIN_REQUIRED' }                      — signed in, not admin
 *
 * The page-level TierGate normally prevents a free user from ever calling a gated
 * endpoint, so a caller only hits these in a race (token expiry, a tier changed
 * mid-session, or a hand-crafted request). This helper lets those callers surface
 * an upgrade / sign-in prompt (the "not-entitled" state) instead of a generic
 * "something went wrong" error — distinct from a "not-built" 404/501.
 */

export const TIER_CODES = {
    AUTH_REQUIRED: 'AUTH_REQUIRED',
    UPGRADE_REQUIRED: 'UPGRADE_REQUIRED',
    ADMIN_REQUIRED: 'ADMIN_REQUIRED',
};

/** True for the HTTP statuses requireTier/requireAdmin use to deny access. */
export function isTierGateStatus(status) {
    return status === 401 || status === 402 || status === 403;
}

/**
 * Map a denied response to a friendly, actionable message. Accepts the HTTP status
 * and the parsed JSON body (either may be absent). Returns null when the response
 * is not a tier-gate denial, so callers can fall through to their normal error path.
 * @param {number} status
 * @param {{ code?: string, requiredTier?: string, currentTier?: string }} [body]
 * @returns {{ kind: 'signin'|'upgrade'|'forbidden', text: string } | null}
 */
export function tierMessage(status, body = {}) {
    const code = body && body.code;
    if (status === 401 || code === TIER_CODES.AUTH_REQUIRED) {
        return { kind: 'signin', text: 'Please sign in to access this feature.' };
    }
    if (status === 402 || code === TIER_CODES.UPGRADE_REQUIRED) {
        return {
            kind: 'upgrade',
            text: 'This is a premium feature. Upgrade your account to unlock it.',
        };
    }
    if (status === 403 || code === TIER_CODES.ADMIN_REQUIRED) {
        return { kind: 'forbidden', text: 'Admin access is required for this feature.' };
    }
    return null;
}
